import React, { useState } from 'react';
import './ContactForm.css';
// Use Amplify outputs to discover REST API URL when available
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - JSON import
import outputs from '../amplify_outputs.json';
import { trackContactForm, trackFormError, trackSpamDetection } from '../utils/matomoEvents';
import type { Schema } from '../amplify/data/resource';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  subject: string;
  message: string;
  // Honeypot fields - hidden from users, bots will fill them
  website: string;
  confirm_email: string;
  // Security metadata
  formStartTime: number;
  // Privacy consent
  acceptPrivacy?: boolean;
  // Custom subject when 'other' is selected
  customSubject?: string;
}

interface FormErrors {
  [key: string]: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    // Honeypot fields - hidden from users, bots will fill them
    website: '',
    confirm_email: '',
    // Security metadata
    formStartTime: Date.now(),
    // Privacy consent
    acceptPrivacy: false,
    // Custom subject
    customSubject: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const { name } = target as HTMLInputElement;
    const fieldValue = (target as HTMLInputElement).type === 'checkbox'
      ? (target as HTMLInputElement).checked
      : (target as HTMLInputElement).value;
    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));
    
    // Clear error when user starts typing/toggling
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    // Security check: Honeypot fields (bots will fill these)
    if (formData.website || formData.confirm_email) {
      trackSpamDetection('Contact Form', 'Honeypot triggered');
      newErrors.security = 'Spam detected. Please try again.';
      return newErrors;
    }

    // Security check: Time-based validation (too fast = bot)
    const submissionTime = Date.now();
    const timeDiff = submissionTime - formData.formStartTime;
    if (timeDiff < 3000) { // Less than 3 seconds
      trackSpamDetection('Contact Form', 'Form submitted too fast');
      newErrors.security = 'Please take more time to fill out the form.';
      return newErrors;
    }

    // Enhanced validation with sanitization
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Name ist zu lang (maximal 100 Zeichen)';
    } else if (!/^[a-zA-ZäöüÄÖÜß\s.-]+$/.test(formData.name.trim())) {
      newErrors.name = 'Name enthält ungültige Zeichen';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    } else if (formData.email.length > 254) {
      newErrors.email = 'E-Mail-Adresse ist zu lang';
    }

    if (formData.company && formData.company.length > 200) {
      newErrors.company = 'Firmenname ist zu lang (maximal 200 Zeichen)';
    }

    if (formData.phone && formData.phone.length > 50) {
      newErrors.phone = 'Telefonnummer ist zu lang';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Betreff ist erforderlich';
    } else if (formData.subject.trim().length > 200) {
      newErrors.subject = 'Betreff ist zu lang (maximal 200 Zeichen)';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Nachricht ist erforderlich';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Nachricht muss mindestens 10 Zeichen lang sein';
    } else if (formData.message.trim().length > 2000) {
      newErrors.message = 'Nachricht ist zu lang (maximal 2000 Zeichen)';
    }

    // If 'other', customSubject required
    if (formData.subject === 'other' && !formData.customSubject?.trim()) {
      newErrors.customSubject = 'Bitte geben Sie einen individuellen Betreff an.';
    }

    // Privacy consent required
    if (!formData.acceptPrivacy) {
      newErrors.acceptPrivacy = 'Bitte stimmen Sie der Datenschutzerklärung zu.';
    }

    // Basic spam content detection
    const spamPatterns = [
      /viagra/i, /casino/i, /lottery/i, /inheritance/i, /prince/i, /million dollars/i,
      /click here/i, /free money/i, /urgent/i, /congratulations/i, /SEO/i, /backlinks/i
    ];
    
    const messageContent = (formData.message + ' ' + (formData.subject === 'other' ? formData.customSubject : formData.subject)).toLowerCase();
    if (spamPatterns.some(pattern => pattern.test(messageContent))) {
      trackSpamDetection('Contact Form', 'Spam content detected');
      newErrors.message = 'Nachricht enthält verdächtige Inhalte';
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSubmitStatus(null);

    try {
      console.log('Contact form submission:', formData);

      // Build payload for backend API - include createdAtIso with current timestamp
      const createdAtIso = new Date().toISOString();
      const payload = {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        subject: formData.subject,
        customSubject: formData.customSubject,
        message: formData.message,
        acceptPrivacy: formData.acceptPrivacy,
        createdAtIso,  // Set timestamp at creation time, not schema deployment time
      };

      // 1) Always create ContactSubmission first
      const { generateClient } = await import('aws-amplify/data');
      const client = generateClient<Schema>({
        authMode: 'iam'
      });
      const created = await client.models.ContactSubmission.create(payload as any);
      const createdId = created?.data?.id as string | undefined;
      
      if (!createdId) throw new Error('ContactSubmission create failed');

      // 2) If function URL is configured, call it to send email
      const functionUrl = (outputs as any)?.custom?.CONTACT_EMAIL_URL || process.env.REACT_APP_CONTACT_URL || '';
      if (functionUrl) {
        const emailPayload = {
          ...payload,
          submissionId: createdId, // Pass the submission ID for processing status update
        };
        
        const res = await fetch(functionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailPayload),
        });
        if (!res.ok) throw new Error(`Contact email failed: ${res.status}`);
        
        // The Lambda function will mark it as processed
      } else {
        // Fallback: mark as processed if no function URL
        await client.models.ContactSubmission.update({ id: createdId, processed: true });
      }

      // Track successful contact form submission
      const subject = formData.customSubject || formData.subject;
      trackContactForm(subject);

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: '',
        website: '',
        confirm_email: '',
        formStartTime: Date.now(),
        acceptPrivacy: false,
        customSubject: ''
      });
      
    } catch (error) {
      console.error('Form submission failed:', error);
      
      // Determine error type for tracking
      let errorType = 'Submission Error';
      let errorMessage = 'Form submission failed';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.message.includes('Network')) {
          errorType = 'Network Error';
        } else if (error.message.includes('timeout')) {
          errorType = 'Timeout Error';
        } else if (error.message.includes('ContactSubmission')) {
          errorType = 'Database Error';
        } else if (error.message.includes('email')) {
          errorType = 'Email Delivery Error';
        }
      }
      
      // Track error in Matomo (GDPR-compliant, no PII)
      trackFormError(errorMessage, {
        formName: 'Contact Form',
        errorType
      });
      
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-form-section" data-section="contact-form">
      <div className="contact-form-container">
        <div className="contact-form-content">
          <div className="contact-info">
            <h2>Kontaktieren Sie uns</h2>
            <p>Haben Sie Fragen zu unseren KI-Kursen oder möchten Sie ein individuelles Angebot? Wir freuen uns auf Ihre Nachricht!</p>
            
            <div className="founders-contact">
              <div className="founder-contact-card">
                <div className="founder-contact-image">
                  <img 
                    src="/assets/images/home/founders/antonia.jpeg" 
                    alt="Antonia Schulte"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNGNUY1RjUiLz4KPGNpcmNsZSBjeD0iNDAiIGN5PSIzMiIgcj0iMTIiIGZpbGw9IiMwMDhGOTkiLz4KPHN0ZXQgZD0iTTIwIDYwQzIwIDUwLjMgMjcuOCA0MyAzNyA0M0g0M0M1Mi4yIDQzIDYwIDUwLjMgNjAgNjBWNjRIMjBWNjBaIiBmaWxsPSIjMDA4Rjk5Ii8+Cjx0ZXh0IHg9IjQwIiB5PSI3MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzMzMzMzMyIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjgiPkFudG9uaWE8L3RleHQ+Cjwvc3ZnPg==`;
                      target.alt = 'Antonia Schulte - Placeholder';
                    }}
                  />
                </div>
                <div className="founder-contact-info">
                  <h3>Antonia Schulte</h3>
                  <div className="role">Didaktische Leitung</div>
                  <div className="specialization">Ansprechpartnerin für Keynotes, Lehrkonzepte und Weiterbildungskurse</div>
                </div>
              </div>

              <div className="founder-contact-card">
                <div className="founder-contact-image">
                  <img 
                    src="/assets/images/home/founders/wilhelm.jpg" 
                    alt="Wilhelm Klat" 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNGNUY1RjUiLz4KPGNpcmNsZSBjeD0iNDAiIGN5PSIzMiIgcj0iMTIiIGZpbGw9IiMwMDhGOTkiLz4KPHN0ZXQgZD0iTTIwIDYwQzIwIDUwLjMgMjcuOCA0MyAzNyA0M0g0M0M1Mi4yIDQzIDYwIDUwLjMgNjAgNjBWNjRIMjBWNjBaIiBmaWxsPSIjMDA4Rjk5Ii8+Cjx0ZXh0IHg9IjQwIiB5PSI3MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzMzMzMzMyIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjgiPldpbGhlbG08L3RleHQ+Cjwvc3ZnPg==`;
                      target.alt = 'Wilhelm Klat - Placeholder';
                    }}
                  />
                </div>
                <div className="founder-contact-info">
                  <h3>Wilhelm Klat</h3>
                  <div className="role">Technische Leitung</div>
                  <div className="specialization">Ansprechpartner für technische Anfragen, KI-Implementierung und industrielle Automatisierung</div>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form">
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    placeholder="Ihr vollständiger Name"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">E-Mail *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="ihre.email@beispiel.de"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Unternehmen/Organisation</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Ihr Unternehmen oder Ihre Organisation"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Telefon</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+49 (0) 123 456 789"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Betreff *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={errors.subject ? 'error' : ''}
                >
                  <option value="">Bitte wählen Sie einen Betreff</option>
                  <option value="ki-kurse">Interesse an KI-Kursen</option>
                  <option value="ki-impulse">Anfrage für KI-Impulse</option>
                  <option value="partnership">Partnerschaft/Kooperation</option>
                  <option value="individual">Individuelle Schulungsanfrage</option>
                  <option value="general">Allgemeine Anfrage</option>
                  <option value="other">Sonstiges</option>
                </select>
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>

              {formData.subject === 'other' && (
                <div className="form-group">
                  <label htmlFor="customSubject">Individueller Betreff *</label>
                  <input
                    type="text"
                    id="customSubject"
                    name="customSubject"
                    value={formData.customSubject}
                    onChange={handleChange}
                    className={errors.customSubject ? 'error' : ''}
                    placeholder="Bitte geben Sie Ihren Betreff an"
                  />
                  {errors.customSubject && <span className="error-message">{errors.customSubject}</span>}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="message">Nachricht *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? 'error' : ''}
                  placeholder="Beschreiben Sie Ihre Anfrage oder Ihre Fragen..."
                  rows={6}
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              {/* Privacy consent inline */}
              <div className="form-group privacy-inline">
                <input
                  type="checkbox"
                  id="acceptPrivacy"
                  name="acceptPrivacy"
                  checked={!!formData.acceptPrivacy}
                  onChange={handleChange}
                />
                <label htmlFor="acceptPrivacy" style={{ margin: 0 }}>
                  Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zu.
                </label>
              </div>
              {errors.acceptPrivacy && <span className="error-message">{errors.acceptPrivacy}</span>}

              {/* Honeypot fields - hidden from users, bots will fill them */}
              <div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
                <label htmlFor="website">Website (leave empty)</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
                <label htmlFor="confirm_email">Confirm Email (leave empty)</label>
                <input
                  type="email"
                  id="confirm_email"
                  name="confirm_email"
                  value={formData.confirm_email}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {errors.security && (
                <div className="status-message error" style={{ marginBottom: '20px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
                  </svg>
                  {errors.security}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Wird gesendet...
                    </>
                  ) : (
                    'Nachricht senden'
                  )}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="status-message success">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                  </svg>
                  Vielen Dank! Wir haben Ihre Nachricht erhalten und melden uns zeitnah bei Ihnen.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="status-message error">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
                  </svg>
                  Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es erneut.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;